import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseShortcut, eventMatches, isEditableTarget } from './shortcuts';

// The keyboard layer. Components register shortcuts with useShortcut(); one
// window listener dispatches them. Shortcuts never fire while typing in a
// field or while a modal is open, unless the registration says otherwise,
// and a chord's first key waits one second for its second.

const ShortcutContext = createContext(null);

const CHORD_TIMEOUT_MS = 1000;
let nextId = 0;

const modalIsOpen = () =>
  typeof document !== 'undefined' && !!document.querySelector('.modal-overlay, [role="dialog"][aria-modal="true"]');

export function ShortcutProvider({ children }) {
  const registry = useRef(new Map());
  const pendingChord = useRef(null);
  const [version, setVersion] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);

  const register = useCallback((entry) => {
    const id = ++nextId;
    registry.current.set(id, { ...entry, parsed: parseShortcut(entry.keys) });
    setVersion((v) => v + 1);
    return () => {
      registry.current.delete(id);
      setVersion((v) => v + 1);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat) return;
      const inInput = isEditableTarget(event.target);
      const inModal = modalIsOpen();
      const now = Date.now();
      const pending = pendingChord.current && now - pendingChord.current.at < CHORD_TIMEOUT_MS ? pendingChord.current : null;
      pendingChord.current = null;

      const entries = [...registry.current.values()].filter((entry) => {
        if (entry.enabled === false) return false;
        if (inInput && !entry.allowInInput) return false;
        if (inModal && !entry.allowInModal) return false;
        return true;
      });

      // A complete match: a single-step shortcut, or the second step of a chord.
      for (const entry of entries) {
        const steps = entry.parsed;
        const hit = steps.length === 1
          ? eventMatches(event, steps[0])
          : pending && pending.key === steps[0].key && eventMatches(event, steps[1]);
        if (hit) {
          event.preventDefault();
          entry.handler(event);
          return;
        }
      }

      // The first step of a chord: remember it and wait.
      if (!event.metaKey && !event.ctrlKey && !event.altKey) {
        for (const entry of entries) {
          if (entry.parsed.length > 1 && eventMatches(event, entry.parsed[0])) {
            pendingChord.current = { key: entry.parsed[0].key, at: now };
            return;
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // What the shortcut sheet shows: everything registered with a description.
  const shortcuts = useMemo(() => [...registry.current.values()].filter((s) => s.description), [version]);

  const value = useMemo(() => ({ register, shortcuts, sheetOpen, setSheetOpen }), [register, shortcuts, sheetOpen]);
  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>;
}

/**
 * Register a shortcut for as long as the component is mounted.
 *
 * @param {string} keys      'mod+k', '?', 'g a'
 * @param {Function} handler
 * @param {Object} [options]
 * @param {string}  [options.description]   shown on the shortcut sheet; omit to keep it unlisted
 * @param {string}  [options.group]         sheet heading, e.g. 'Navigate'
 * @param {boolean} [options.enabled=true]
 * @param {boolean} [options.allowInInput]  fire even while typing (for mod+ combos)
 * @param {boolean} [options.allowInModal]  fire even while a modal is open
 */
export function useShortcut(keys, handler, options = {}) {
  const context = useContext(ShortcutContext);
  const register = context ? context.register : null;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const { description, group, enabled, allowInInput, allowInModal } = options;

  useEffect(() => {
    if (!register || !keys) return undefined;
    return register({ keys, description, group, enabled, allowInInput, allowInModal, handler: (e) => handlerRef.current(e) });
  }, [register, keys, description, group, enabled, allowInInput, allowInModal]);
}

/** The registered shortcuts and the sheet's open state, for the sheet itself. */
export function useShortcuts() {
  return useContext(ShortcutContext) || { register: null, shortcuts: [], sheetOpen: false, setSheetOpen: () => {} };
}
